import { clsx } from 'clsx';
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../i18n';
import styles from './ui.module.css';

/**
 * Длинный текст, свёрнутый до трёх строк.
 *
 * Заводится потому, что описания в игре пишут абзацами: биография на экран,
 * заметка о внешке в две тысячи знаков, разбор способности. Целиком они
 * раздувают карточку так, что до всего остального приходится листать, а
 * обрезать их молча нельзя — человек не узнает, что текст был длиннее.
 *
 * Поэтому обрезаем ВИДИМО: три строки и подпись «читать всё». Свёрнутое
 * состояние — начальное, и это тоже осознанно: экран должен открываться
 * коротким, а разворачивает его тот, кому этот текст нужен.
 *
 * Кнопка появляется, только если текст и правда не влез: у описания в одну
 * строку она была бы обманом. Проверяем это замером после отрисовки —
 * посчитать число строк заранее нельзя, оно зависит от ширины экрана.
 */
export function ReadMore({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (el === null) return undefined;

    // Сравниваем полную высоту с видимой. Замер повторяется на изменение
    // ширины: поворот телефона меняет число строк, а с ним и ответ.
    const measure = () => setClipped(el.scrollHeight - el.clientHeight > 1);
    measure();

    // Наблюдатель ловит и смену текста, и смену ширины: и то и другое меняет
    // размер элемента. Класть сюда children незачем — они меняют ссылку на
    // каждый рендер, и замер шёл бы вхолостую.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <div
        id={id}
        ref={bodyRef}
        className={clsx(styles.readMoreBody, !open && styles.readMoreClamp)}
      >
        {children}
      </div>

      {clipped || open ? (
        <button
          type="button"
          className={styles.readMoreToggle}
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? t('ui.readMore.collapse') : t('ui.readMore.expand')}
        </button>
      ) : null}
    </div>
  );
}
